import { useEffect, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import scottAvatar from "@/assets/scott-avatar.webp";

type Msg = { role: "user" | "assistant"; content: string };

const STARTERS = [
  "What kind of companies has Scott worked with?",
  "Summarize Scott's experience with AI and GPU infrastructure.",
  "What results has Scott driven?",
  "Would Scott be a fit for a Web3 content lead role?",
  "What services does Scott offer?",
  "Write me a short intro I could send to my team about Scott.",
];

const LOCAL_LIMIT_KEY = "ask-scott-usage";
const LOCAL_DAILY_CAP = 5;

/** Custom GPT fallback when users hit the daily cap. */
const CUSTOM_GPT_URL = "https://chatgpt.com/g/g-6a21be3b47f8819180a4392655e5cdc3-ask-scott";

/** Replace em/en/figure dashes and minus signs with a plain comma+space or hyphen. */
function stripLongDashes(text: string): string {
  return text
    .replace(/\s*[\u2014\u2015]\s*/g, ", ")
    .replace(/\s*[\u2013\u2212]\s*/g, ", ")
    .replace(/\u2010|\u2011/g, "-");
}

type LocalUsage = { day: string; count: number };

function readUsage(): LocalUsage {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const raw = localStorage.getItem(LOCAL_LIMIT_KEY);
    if (!raw) return { day: today, count: 0 };
    const parsed = JSON.parse(raw) as LocalUsage;
    if (parsed.day !== today) return { day: today, count: 0 };
    return parsed;
  } catch {
    return { day: today, count: 0 };
  }
}

function writeUsage(u: LocalUsage) {
  try {
    localStorage.setItem(LOCAL_LIMIT_KEY, JSON.stringify(u));
  } catch {
    /* ignore */
  }
}

function parseGoto(text: string): { body: string; goto: string | null; label: string | null } {
  // Accept either an on-site path (/foo) or a full external URL (https://...)
  const m = text.match(/\[goto:((?:https?:\/\/|\/)[^\s\]|]+)(?:\|([^\]]+))?\]\s*$/);
  if (!m) return { body: text, goto: null, label: null };
  return { body: text.slice(0, m.index).trim(), goto: m[1], label: m[2]?.trim() || null };
}

type Props = { onClose: () => void };

export function AskScottPanel({ onClose }: Props) {
  const router = useRouter();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState<number>(LOCAL_DAILY_CAP);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const u = readUsage();
    setRemaining(Math.max(0, LOCAL_DAILY_CAP - u.count));
    inputRef.current?.focus();
    // Sync with server-side (per-IP) usage so the UI is honest across browsers/devices
    (async () => {
      try {
        const res = await fetch("/api/public/ask-scott", { method: "GET" });
        if (!res.ok) return;
        const data = (await res.json()) as { remaining?: number; limit?: number };
        if (typeof data.remaining === "number" && typeof data.limit === "number") {
          setRemaining(data.remaining);
          const today = new Date().toISOString().slice(0, 10);
          writeUsage({ day: today, count: Math.max(0, data.limit - data.remaining) });
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const assistantReplies = messages.filter((m) => m.role === "assistant").length;

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    const usage = readUsage();
    if (usage.count >= LOCAL_DAILY_CAP) {
      setError(
        "You've hit your daily question limit. Reach out via the contact page to talk to the real Scott.",
      );
      return;
    }
    setError(null);
    const next: Msg[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/public/ask-scott", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next }),
      });
      const data = (await res.json()) as { text?: string; error?: string; remaining?: number };
      if (!res.ok) {
        if (typeof data.remaining === "number") {
          setRemaining(data.remaining);
          const today = new Date().toISOString().slice(0, 10);
          writeUsage({ day: today, count: Math.max(0, LOCAL_DAILY_CAP - data.remaining) });
        }
        setError(data.error ?? "Something went wrong. Try again.");
        setMessages(messages);
        return;
      }
      if (typeof data.remaining === "number") {
        setRemaining(data.remaining);
        writeUsage({ day: usage.day, count: Math.max(0, LOCAL_DAILY_CAP - data.remaining) });
      } else {
        const newUsage = { day: usage.day, count: usage.count + 1 };
        writeUsage(newUsage);
        setRemaining(Math.max(0, LOCAL_DAILY_CAP - newUsage.count));
      }
      setMessages([...next, { role: "assistant", content: stripLongDashes(data.text ?? "") }]);
    } catch {
      setError("Network error. Try again.");
      setMessages(messages);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Ask Scott chat"
      className={
        "ask-scott-panel flex flex-col overflow-hidden rounded-2xl border-2 border-foreground bg-background shadow-pop transition-[width,height] duration-200 " +
        (expanded
          ? "h-[min(820px,calc(100vh-4rem))] w-[min(560px,calc(100vw-2rem))]"
          : "h-[min(620px,calc(100vh-6rem))] w-[min(380px,calc(100vw-2rem))]")
      }
    >
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-border bg-background px-4 py-3">
        <a
          href={CUSTOM_GPT_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          <img
            src={scottAvatar}
            alt=""
            aria-hidden
            width={36}
            height={36}
            className="h-9 w-9 rounded-full border border-border object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="font-display text-base leading-tight">Ask Scott</p>
            <p className="text-[11px] text-muted-foreground">
              AI version of Scott, trained on his work
            </p>
          </div>
        </a>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Shrink chat" : "Expand chat"}
          title={expanded ? "Shrink" : "Expand"}
          className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            {expanded ? (
              <path
                d="M9 3v4h4M3 13l5-5M7 13H3v-4M13 3L8 8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M3 7V3h4M13 9v4H9M3 3l5 5M13 13l-5-5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && remaining > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Hey, I'm an AI version of Scott. Ask me anything about his experience, services, case
              studies, or whether he's a fit for your team.
            </p>
            <div className="flex flex-col gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  disabled={loading || remaining <= 0}
                  className="rounded-xl border border-border bg-muted/30 px-3 py-2 text-left text-[13px] leading-snug transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          if (m.role === "user") {
            return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-foreground px-3 py-2 text-sm text-background">
                  {m.content}
                </div>
              </div>
            );
          }
          const { body, goto, label } = parseGoto(m.content);
          return (
            <div key={i} className="flex justify-start">
              <div className="max-w-[90%] space-y-2">
                <div className="ask-scott-md rounded-2xl rounded-bl-sm border border-border bg-muted/40 px-3 py-2 text-sm leading-relaxed">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      ul: ({ children }) => (
                        <ul className="mb-2 ml-4 list-disc space-y-0.5 last:mb-0">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="mb-2 ml-4 list-decimal space-y-0.5 last:mb-0">{children}</ol>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-foreground">{children}</strong>
                      ),
                      a: ({ children, href }) => {
                        const safe = /^(https?:|mailto:|tel:|\/)/i.test(href ?? "") ? href : "#";
                        return (
                          <a
                            href={safe}
                            className="underline underline-offset-2 hover:text-primary"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {body}
                  </ReactMarkdown>
                </div>
                {goto && (
                  <button
                    type="button"
                    onClick={() => {
                      if (/^https?:\/\//i.test(goto)) {
                        window.open(goto, "_blank", "noopener,noreferrer");
                      } else {
                        onClose();
                        router.navigate({ to: goto as never });
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-foreground/20 bg-background px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-foreground hover:text-background"
                  >
                    {label ?? "Take me there"} <span aria-hidden>→</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm border border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="ask-scott-dots inline-flex gap-1">
                <span />
                <span />
                <span />
              </span>
            </div>
          </div>
        )}

        {remaining <= 0 && !loading && (
          <div className="rounded-xl border-2 border-foreground bg-gradient-acid px-3 py-3 text-sm space-y-2">
            <p className="font-medium">You've used today's 5 questions.</p>
            <p className="text-[12px] leading-snug">
              Want to keep going? You can ask Scott more questions through the full GPT, or book a
              call with the real Scott.
            </p>
            <div className="flex flex-wrap justify-center gap-2 pt-1">
              <a
                href={CUSTOM_GPT_URL}
                target="_blank"
                rel="noreferrer"
                className="lift-glow inline-flex items-center gap-1 rounded-full border-2 border-foreground bg-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-background hover:bg-background hover:text-foreground"
              >
                Ask Scott More <span aria-hidden>↗</span>
              </a>
              <a
                href="https://calendly.com/scottthomascunningham/30min"
                target="_blank"
                rel="noreferrer"
                className="lift-glow inline-flex items-center gap-1 rounded-full border-2 border-foreground bg-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-background hover:bg-background hover:text-foreground"
              >
                Book a Call <span aria-hidden>↗</span>
              </a>
              <div className="flex w-full justify-center">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    router.navigate({ to: "/contact" });
                  }}
                  className="lift-glow inline-flex items-center gap-1 rounded-full bg-gradient-hero px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-primary-foreground shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)]"
                >
                  Let's Connect <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {remaining > 0 && assistantReplies >= 2 && !loading && (
          <div className="rounded-xl border border-foreground/20 bg-muted/30 px-3 py-3 text-[12px] space-y-2">
            <p className="text-center">Want to talk to the real Scott?</p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  router.navigate({ to: "/contact" });
                }}
                className="lift-glow inline-flex items-center gap-1 rounded-full border-2 border-foreground bg-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-background hover:bg-background hover:text-foreground"
              >
                Get in touch <span aria-hidden>→</span>
              </button>
              <a
                href="https://calendly.com/scottthomascunningham/30min"
                target="_blank"
                rel="noreferrer"
                className="lift-glow inline-flex items-center gap-1 rounded-full border-2 border-foreground bg-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-background hover:bg-background hover:text-foreground"
              >
                Book a call <span aria-hidden>↗</span>
              </a>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="border-t border-border bg-background px-3 py-3"
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            placeholder={remaining > 0 ? "Ask about Scott's work..." : "Daily limit reached"}
            disabled={loading || remaining <= 0}
            rows={1}
            className="min-h-[40px] max-h-32 flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || remaining <= 0}
            aria-label="Send"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-foreground text-background transition-opacity disabled:opacity-40"
          >
            <svg
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M2 8h12M9 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <p className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {remaining} {remaining === 1 ? "question" : "questions"} left today
          </span>
          <span>AI may be inaccurate</span>
        </p>
      </form>
    </div>
  );
}
